// src/redux/selectors/residualRiskSelectors.js

/**
 * Find the risk object in the store by scanning modelRisks and explorerRisks for all sections & categories.
 */
export function findRiskById(state, assessmentUuid, riskId) {
    // Try RTK slice first (riskItems), fall back to legacy reducer (risks)
    // RTK slice is populated by fetchAssessmentRisks thunk (autopilot flow)
    // Legacy reducer is populated by FETCH_ASSESSMENT_RISKS_SUCCESS action (manual flow)
    const modelRisks = state.riskItems?.byAssessment[assessmentUuid]?.modelRisks
                    || state.risks?.byAssessment[assessmentUuid]?.modelRisks
                    || {};

    // Also check explorerRisks for Explorer assessments
    const explorerRisks = state.riskItems?.byAssessment[assessmentUuid]?.explorerRisks
                       || state.risks?.byAssessment[assessmentUuid]?.explorerRisks
                       || {};

    // Helper to search within a risks structure
    const searchInRisks = (risksObj) => {
      for (const [sectionKey, sectionObj] of Object.entries(risksObj)) {
        const { areaRisks = {}, riskSources = [] } = sectionObj;
        // Each section has an object of categories => array of risks
        for (const [categoryName, risksArr] of Object.entries(areaRisks)) {
          const matchedRisk = risksArr?.find((r) => r.riskId === riskId);
          if (matchedRisk) {
            return {
              sectionName: sectionKey,
              categoryName,
              risk: matchedRisk,
              riskSources,
            };
          }
        }
      }
      return null;
    };

    // Search modelRisks first, then explorerRisks
    return searchInRisks(modelRisks) || searchInRisks(explorerRisks);
  }

  /**
   * Build the "riskData" object with all relevant fields: priority, rationale, name, etc.
   * Also incorporate the associated riskSources for the section.
   */
  export function selectRiskDetails(state, assessmentUuid, riskId) {
    const found = findRiskById(state, assessmentUuid, riskId);
    if (!found) return null;
  
    const { risk, sectionName, categoryName, riskSources } = found;
    return {
      riskId: risk.riskId,
      riskName: risk.riskName,
      priority: risk.priority,
      rationale: risk.rationale,
      type: risk.type,
      status: risk.status,
      evaluationData: risk.evaluationData || {},
      // You can add the full riskSources for more context
      riskSources,
      // If you want *only the relevant riskSource* for that category, you can filter riskSources by the name of category
      // but typically "riskSources" is the entire array describing the section's causes, etc.
      sectionName,
      categoryName,
    };
  }
  
  /**
   * Return an array of controls for the given riskId, including any evaluation details.
   */
  export function selectControlsForRisk(state, riskId) {
    return state.controls.savedControls[riskId] || [];
  }

  // src/redux/selectors/residualRiskSelectors.js

/**
 * Combines saved controls for a risk with their corresponding evaluations.
 */
export function buildActionPlanData(state, riskId) {
  const controls = state.controls.savedControls[riskId] || [];
  const evaluations = state.controls.savedControlEvaluations[riskId] || {};

  return controls.map((ctrl) => {
    const ctrlEvaluations = evaluations[ctrl.controlId] || [];
    return { ...ctrl, evaluations: ctrlEvaluations };
  });
}